import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { getTrackerDevices } from "../utils";

export const useTrackerDevices = (deviceTrackerUid, date) => {
  const [trackerRecords, setTrackerRecords] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTrackerRecords = useCallback(async () => {
    if (!deviceTrackerUid) {
      setTrackerRecords([]);
      return;
    }

    // Calcular rango del día seleccionado si se proporciona fecha
    const startDateIso = date ? dayjs(date).startOf("day").toISOString() : undefined;
    const endDateIso = date ? dayjs(date).endOf("day").toISOString() : undefined;

    try {
      setLoading(true);
      setError(null);
      setTrackerRecords([]);
      const token = sessionStorage.getItem("token");
      const devicesData = await getTrackerDevices(token, deviceTrackerUid, {
        startDateIso,
        endDateIso,
      });

      // Debug: ver qué está devolviendo la API
      console.log("API Response for tracker devices:", devicesData);
      console.log("Type of devicesData:", typeof devicesData);
      console.log("Is Array:", Array.isArray(devicesData));

      // Manejar diferentes estructuras de respuesta
      let records = [];
      if (Array.isArray(devicesData)) {
        records = devicesData;
      } else if (devicesData && typeof devicesData === "object") {
        // Si es un objeto, buscar propiedades que puedan contener el array
        if (Array.isArray(devicesData.items)) {
          records = devicesData.items;
        } else if (Array.isArray(devicesData.data)) {
          records = devicesData.data;
        } else if (Array.isArray(devicesData.records)) {
          records = devicesData.records;
        } else if (Array.isArray(devicesData.trackers)) {
          records = devicesData.trackers;
        } else {
          // Si no encontramos un array, convertir el objeto en array
          records = [devicesData];
        }
      }

      setTrackerRecords(records);
      // Obtener la ubicación del registro más reciente (suponemos orden cronológico del backend o lo ordenamos).
      if (records.length > 0) {
        // Ordenar por fecha de creación descendente para asegurar que el primero sea el más reciente
        const sorted = [...records].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const { gps } = sorted[0];
        const lat = parseFloat(gps?.latitude);
        const lng = parseFloat(gps?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setCurrentLocation({ lat, lng });
        } else {
          setCurrentLocation(null);
        }
      } else {
        setCurrentLocation(null);
      }
    } catch (err) {
      console.error("Error fetching tracker devices:", err);
      setError(err.message || "Error al obtener dispositivos de tracking");
      setTrackerRecords([]);
    } finally {
      setLoading(false);
    }
  }, [deviceTrackerUid, date]);

  const refreshDevices = useCallback(() => {
    fetchTrackerRecords();
  }, [fetchTrackerRecords]);

  // Agregar un nuevo registro de forma incremental (socket u otras fuentes)
  const addRecord = useCallback(
    (record) => {
      if (!record) return;

      // Validate createdAt before processing
      if (!record.createdAt) {
        console.warn("[useTrackerDevices] Record without createdAt, skipping");
        return;
      }

      const createdAtDate = dayjs(record.createdAt);
      if (!createdAtDate.isValid()) {
        console.warn("[useTrackerDevices] Record with invalid createdAt:", record.createdAt);
        return;
      }

      // Filtrar por fecha
      if (date) {
        const sameDay = createdAtDate.isSame(dayjs(date), "day");
        if (!sameDay) return; // ignorar registros fuera del día seleccionado
      }

      const lat = parseFloat(record?.gps?.latitude);
      const lng = parseFloat(record?.gps?.longitude);

      // Si las coordenadas no son válidas, ignorar
      if (isNaN(lat) || isNaN(lng)) return;

      setTrackerRecords((prev) => [...prev, record]);

      // actualizar ubicación actual siempre
      setCurrentLocation({ lat, lng });
    },
    [date]
  );

  useEffect(() => {
    fetchTrackerRecords();
  }, [fetchTrackerRecords]);

  return {
    trackerRecords,
    devices: trackerRecords,
    currentLocation,
    loading,
    error,
    refreshDevices,
    fetchTrackerRecords,
    addRecord,
  };
};
